/**
 * KPI tone helper — Landing/Analysis Polish 1.
 *
 * Centralised semantic-tone evaluation for the real Analyze-page KPI
 * grid.  Returns one of:
 *
 *   "positive" — the KPI is genuinely strong (evidence-backed).
 *   "neutral"  — insufficient evidence to claim a positive signal,
 *                or the metric is a pure scale value.
 *
 * Contract:
 *   * Uses ONLY canonical evidence already produced by the backend
 *     valuation engine (``sub_scores``, ``classification``,
 *     ``ai_score``, ``valuation_unit_status``).  This helper never
 *     duplicates financial logic — a KPI that the backend already
 *     considers weak cannot be marked green here.
 *   * Missing, non-finite, or unreconciled monetary values are
 *     ALWAYS neutral — never fabricated positives.
 *   * Scale values (market_cap, revenue, eps) are ALWAYS neutral.
 *   * The Analysis Preview does NOT go through this helper; it keeps
 *     its curated static styling.
 */

const POS = "positive";
const NEU = "neutral";

// Canonical "no-green" classifications — the KPI grid must never
// contradict the backend classification for the same analysis.
const NON_POSITIVE_CANONICAL = new Set(["risk", "unavailable"]);

function _finite(n) {
  if (n === null || n === undefined) return false;
  const v = Number(n);
  return Number.isFinite(v);
}

function _num(n, fallback = null) {
  if (!_finite(n)) return fallback;
  return Number(n);
}

/**
 * Return the semantic tone for a single KPI.
 *
 * @param {string} metricKey  One of: market_cap, pe, forward_pe, eps,
 *   revenue, revenue_growth, earnings_growth, profit_margin,
 *   dividend_yield, roic, fcf_quality, sector_percentile.
 * @param {number|null|undefined} value  The raw metric value.
 * @param {object} ctx  Canonical analysis context:
 *   {
 *     classification,          // canonical: top_opportunity|opportunity|fair|neutral|risk|unavailable
 *     ai_score,                // 0..100 or null
 *     sub_scores,              // { valuation, quality, growth, safety, health }
 *     valuation_unit_status,   // "reconciled" | "unreconciled_*" | null
 *   }
 */
export function getKpiTone(metricKey, value, ctx = {}) {
  if (!metricKey) return NEU;
  if (!_finite(value)) return NEU;

  // Global gates — an unreconciled valuation, a risk/unavailable
  // canonical classification, or a missing analysis context must
  // never produce green.  These rules mirror the null-truthfulness
  // and canonical-consistency contracts.
  const unitStatus = ctx.valuation_unit_status;
  if (unitStatus && String(unitStatus).toLowerCase() !== "reconciled") return NEU;
  const canon = String(ctx.classification || "").toLowerCase();
  if (NON_POSITIVE_CANONICAL.has(canon)) return NEU;

  const sub = ctx.sub_scores || {};
  const quality = _num(sub.quality);
  const growth = _num(sub.growth);
  const valuation = _num(sub.valuation);
  const aiScore = _num(ctx.ai_score);
  const v = Number(value);

  switch (metricKey) {
    // Scale values — never positive purely because they are large.
    case "market_cap":
    case "revenue":
    case "eps":
      return NEU;

    // Valuation multiples — neutral unless the canonical valuation
    // sub-score already identifies a positive valuation AND the raw
    // multiple is a plausible positive number.  A negative or zero
    // multiple never becomes green.
    case "pe":
    case "forward_pe":
      if (!(v > 0)) return NEU;
      if (valuation !== null && valuation >= 70) return POS;
      return NEU;

    // Growth metrics — require BOTH a meaningful raw growth
    // (≥ 10%) AND canonical growth strength (≥ 60/100).  A negative
    // or missing growth value stays neutral even if the sub-score
    // is high (never contradict a negative raw driver).
    case "revenue_growth":
    case "earnings_growth":
      if (!(v >= 0.10)) return NEU;
      if (growth !== null && growth >= 60) return POS;
      return NEU;

    // Profitability + capital efficiency — require both a strong
    // raw value AND the canonical Business Quality sub-score to
    // confirm.  Sector-relative benchmarks are folded into the
    // backend quality score already, so this check IS sector-aware.
    case "profit_margin":
      if (!(v >= 0.15)) return NEU;
      if (quality !== null && quality >= 65) return POS;
      return NEU;
    case "roic":
      if (!(v >= 0.15)) return NEU;
      if (quality !== null && quality >= 65) return POS;
      return NEU;

    // FCF Quality — the raw value IS the canonical sub-score
    // (mapped 0..100), so no additional confirmation needed.
    // Anything ≥ 70 is a strong FCF quality result.
    case "fcf_quality":
      if (v >= 70) return POS;
      return NEU;

    // Sector Percentile — 0..100 with higher = better.  Top 25%
    // (score ≥ 75) is a defensible positive.  Requires ai_score
    // support from the canonical classification.
    case "sector_percentile":
      if (v >= 75 && aiScore !== null && aiScore >= 75) return POS;
      return NEU;

    // Dividend Yield — neutral in v1.  A high yield can equally
    // signal distress; without a canonical positive driver we
    // stay truthfully neutral.
    case "dividend_yield":
      return NEU;

    default:
      return NEU;
  }
}

// Convenience — return the Tailwind text-color class for use as the
// KpiCard ``accent`` prop.  Green mirrors the Stockaily success token.
export function kpiToneAccentClass(tone) {
  return tone === POS ? "text-[#3FAE89]" : "";
}
